import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Button, Upload, Select, Checkbox, Tooltip } from 'antd';
import { InboxOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { Option } = Select;
import { toast } from 'react-toastify';
import type { UploadFile } from 'antd';

import FormItemInputComponent from './FormItemInputComponent';
import AnalyzingModal from './AnalyzingModal';
import DrawingConfigModal from './DrawingConfigModal';

import {
  updateFile,
  replaceDrawing,
  reanalyzeDrawing,
  getFile,
  convertDrawing,
} from '../../Network/Core/Directory/DirectoryServices';
import { getErrorMessage } from '../../Utils/errorHelpers';

interface EditFileModalProps {
  open: boolean;
  file: any | null;
  onClose: () => void;
  onSuccess: () => void;
  onFileUpdate?: (
    fileId: string,
    updates: { status?: string; name?: string; updatedAt?: string },
  ) => void;
  siblingNames?: string[];
}

export const EditFileModal: React.FC<EditFileModalProps> = ({
  open,
  file,
  onClose,
  onSuccess,
  onFileUpdate,
  siblingNames = [],
}) => {
  const [form] = Form.useForm();
  const [name, setName] = useState('');
  const [newFile, setNewFile] = useState<File | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [wantReconvert, setWantReconvert] = useState(false);
  const [keepAsBuilt, setKeepAsBuilt] = useState(true);
  const [simplifyThreshold, setSimplifyThreshold] = useState(0.001);
  const [saving, setSaving] = useState(false);

  // Two-step flow state
  const [showAnalyzing, setShowAnalyzing] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [converting, setConverting] = useState(false); // Hide modal during conversion
  const [currentDrawingId, setCurrentDrawingId] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize form when file changes or modal opens
  useEffect(() => {
    if (open && file) {
      setName(file.name || '');
      form.setFieldsValue({
        fileName: file.name || '',
        simplifyThreshold: 0.001,
      });
      setNewFile(null);
      setFileList([]);
      setWantReconvert(false);
      setKeepAsBuilt(true);
      setSimplifyThreshold(0.001);
    }
  }, [open, file, form]);

  // Cleanup progress interval
  useEffect(() => {
    if (!showAnalyzing && progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, [showAnalyzing]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleFileChange = ({ fileList }: { fileList: UploadFile[] }) => {
    setFileList(fileList.slice(-1));
  };

  const beforeUpload = (uploadFile: any) => {
    const fileSize = uploadFile.size || 0;

    if (fileSize > 1024 * 1024 * 20) {
      form.setFields([
        {
          name: 'file',
          errors: ['File must be smaller than 20MB'],
        },
      ]);
      return Upload.LIST_IGNORE;
    }

    const fileExtension = uploadFile.name
      .substring(uploadFile.name.lastIndexOf('.'))
      .toLowerCase();
    const allowedExtensions = ['.dwg'];
    if (!allowedExtensions.includes(fileExtension)) {
      form.setFields([
        {
          name: 'file',
          errors: [
            'File type is not supported. Please upload a valid DWG file.',
          ],
        },
      ]);
      return Upload.LIST_IGNORE;
    }

    setNewFile(uploadFile);
    setFileList([uploadFile]);
    return false; // Prevent auto upload
  };

  const handleRemoveFile = () => {
    setNewFile(null);
    setFileList([]);
  };

  const handleSave = async () => {
    if (newFile) {
      // Replace DWG flow - upload new file
      await handleReplaceDwg();
    } else if (wantReconvert) {
      // Re-convert existing DWG with new settings
      await handleReconvert();
    } else {
      // Name-only change
      if (name !== file?.name && name.trim() !== '') {
        setSaving(true);
        try {
          await updateFile(file.id, { name: name.trim() });
          toast.success('File updated successfully');
          if (onFileUpdate) {
            onFileUpdate(file.id, {
              name: name.trim(),
              updatedAt: new Date().toISOString(),
            });
          }
          handleClose();
        } catch (error: any) {
          toast.error(getErrorMessage(error, 'Failed to update file'));
        } finally {
          setSaving(false);
        }
      } else {
        // Nothing changed, just close
        handleClose();
      }
    }
  };

  const handleReplaceDwg = async () => {
    if (!newFile || !file) return;

    // Close main modal and show analyzing modal
    setShowAnalyzing(true);
    setUploadProgress(0);

    // Start progress simulation
    progressIntervalRef.current = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 95) return 95;
        if (prev < 30) return prev + 3;
        if (prev < 60) return prev + 2;
        if (prev < 80) return prev + 1;
        return prev + 0.5;
      });
    }, 800);

    try {
      const formData = new FormData();
      formData.append('file', newFile);
      formData.append('keepAsBuilt', String(keepAsBuilt));
      formData.append('simplifyThreshold', String(simplifyThreshold));
      // Include name if changed
      if (name !== file.name && name.trim() !== '') {
        formData.append('name', name.trim());
      }

      const response = await replaceDrawing(file.id, formData);

      if (!response?.data?.id) {
        throw new Error('Replace failed');
      }

      setCurrentDrawingId(response.data.id);

      // Poll for analysis completion
      await pollForAnalysis(response.data.id);
    } catch (error: any) {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setShowAnalyzing(false);
      toast.error(getErrorMessage(error, 'Failed to upload file. Please try again.'));
    }
  };

  const handleReconvert = async () => {
    if (!file) return;

    // Show analyzing modal
    setShowAnalyzing(true);
    setUploadProgress(0);

    // Start progress simulation (faster since no upload)
    progressIntervalRef.current = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 95) return 95;
        if (prev < 50) return prev + 5;
        if (prev < 80) return prev + 2;
        return prev + 1;
      });
    }, 500);

    try {
      const response = await reanalyzeDrawing(file.id, {
        keepAsBuilt,
        simplifyThreshold,
        ...(name !== file.name && name.trim() !== ''
          ? { name: name.trim() }
          : {}),
      });

      if (!response?.data?.id) {
        throw new Error('Reanalyze failed');
      }

      setCurrentDrawingId(response.data.id);

      // Poll for analysis completion
      await pollForAnalysis(response.data.id);
    } catch (error: any) {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setShowAnalyzing(false);
      // Files uploaded before the new conversion service don't have
      // newServiceInputPath set — the new worker can't re-analyze them.
      // Tell the user to re-upload so the file lands in the new bucket.
      const isLegacyFile = !file?.newServiceInputPath;
      if (isLegacyFile) {
        toast.info(
          'This file was uploaded with the old converter. Please upload the DWG again to re-analyze with new settings.',
          { autoClose: 8000 },
        );
      } else {
        toast.error(getErrorMessage(error, 'Failed to analyze file. Please try again.'));
      }
    }
  };

  const pollForAnalysis = async (drawingId: string) => {
    const maxAttempts = 60; // 3 minutes
    const pollInterval = 3000;
    let consecutiveErrors = 0;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));

      try {
        const response = await getFile(drawingId);
        const fileData = response?.data;
        consecutiveErrors = 0;

        if (!fileData) continue;

        if (fileData.status === 'AwaitingConfig') {
          // Stop progress and show 100% briefly
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          setUploadProgress(100);
          await new Promise((resolve) => setTimeout(resolve, 500));

          setShowAnalyzing(false);
          setAnalysisResult(fileData.analysisResult);
          setShowConfig(true);
          return;
        } else if (fileData.status === 'Failed') {
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          setShowAnalyzing(false);
          toast.error(
            fileData.failMessage || 'Analysis failed. Please try again.',
          );
          return;
        }
        // Still analyzing, continue polling
      } catch (error) {
        consecutiveErrors++;
        if (consecutiveErrors >= 5) {
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          setShowAnalyzing(false);
          toast.error('Lost connection to the server. Please refresh and try again.');
          return;
        }
      }
    }

    // Timeout
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    setShowAnalyzing(false);
    toast.error('Analysis timed out. Please try again.');
  };

  const handleConfigConfirm = async (config: {
    sourceUnit?: string;
    excludeLayers: string[];
    hideLayers: string[];
  }) => {
    if (!currentDrawingId) return;

    setShowConfig(false);
    setConverting(true); // Hide main modal during conversion

    // Update the file's status in-place (no full list refresh)
    // Use file.id (original file) to update the list
    if (file?.id && onFileUpdate) {
      onFileUpdate(file.id, { status: 'Processing' });
    }

    const toastId = toast.loading('Uploading drawing...');

    const requestBody = {
      ...config,
      keepAsBuilt,
    };

    try {
      const response = await convertDrawing(currentDrawingId, requestBody);

      if (response?.data?.status === 'Processing') {
        // Poll for conversion completion
        await pollForConversion(currentDrawingId, toastId);
      } else {
        toast.update(toastId, {
          render: response?.data?.error || 'Conversion failed',
          type: 'error',
          isLoading: false,
          autoClose: 5000,
        });
        setConverting(false);
      }
    } catch (error) {
      console.error('Convert error:', error);
      toast.update(toastId, {
        render: 'Conversion failed. Please try again.',
        type: 'error',
        isLoading: false,
        autoClose: 5000,
      });
      setConverting(false);
    }

    setCurrentDrawingId(null);
    setAnalysisResult(null);
  };

  const pollForConversion = async (drawingId: string, toastId: any) => {
    const maxAttempts = 60; // 3 minutes
    const pollInterval = 3000;
    let consecutiveErrors = 0;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));

      try {
        const response = await getFile(drawingId);
        const fileData = response?.data;
        consecutiveErrors = 0;

        if (!fileData) continue;

        if (fileData.status === 'Completed') {
          toast.update(toastId, {
            render: 'Drawing replaced successfully!',
            type: 'success',
            isLoading: false,
            autoClose: 3000,
          });
          setConverting(false);
          if (onFileUpdate && file?.id) {
            onFileUpdate(file.id, {
              status: 'Completed',
              updatedAt: new Date().toISOString(),
            });
          }
          handleClose();
          return;
        } else if (fileData.status === 'Failed') {
          toast.update(toastId, {
            render:
              fileData.failMessage ||
              'Conversion failed. Original file preserved.',
            type: 'error',
            isLoading: false,
            autoClose: 5000,
          });
          setConverting(false);
          if (onFileUpdate && file?.id) {
            onFileUpdate(file.id, { status: 'Completed' });
          }
          handleClose();
          return;
        }
        // Still processing, continue polling
      } catch (error) {
        consecutiveErrors++;
        if (consecutiveErrors >= 5) {
          toast.update(toastId, {
            render: 'Lost connection. Please refresh to check the status.',
            type: 'warning',
            isLoading: false,
            autoClose: 5000,
          });
          setConverting(false);
          handleClose();
          return;
        }
      }
    }

    // Timeout
    toast.update(toastId, {
      render: 'Conversion is taking longer than expected. Please refresh to check the status.',
      type: 'warning',
      isLoading: false,
      autoClose: 5000,
    });
    setConverting(false);
    handleClose();
  };

  const handleConfigCancel = async () => {
    // In edit flow, DON'T delete the file - just revert status and close
    // (Unlike new upload flow in ProjectViewDetail, here we're editing an EXISTING file)
    // Revert the file status back to Completed (both local state AND backend)
    if (file?.id) {
      // Update local state immediately
      if (onFileUpdate) {
        onFileUpdate(file.id, { status: 'Completed' });
      }
      // Also update backend so status persists after navigation
      try {
        await updateFile(file.id, { status: 'Completed' });
      } catch (error) {
        console.error('Failed to revert file status:', error);
      }
    }

    toast.info('Edit cancelled');

    // Close everything at once (handleClose resets all state including showConfig)
    handleClose();
  };

  const handleClose = () => {
    // Reset state
    setName('');
    setNewFile(null);
    setFileList([]);
    setWantReconvert(false);
    setKeepAsBuilt(true);
    setSimplifyThreshold(0.001);
    setShowAnalyzing(false);
    setShowConfig(false);
    setConverting(false);
    setCurrentDrawingId(null);
    setAnalysisResult(null);
    setUploadProgress(0);
    form.resetFields();
    onClose();
  };

  const isNameValid = name.trim().length > 0 && name.trim().length <= 50;
  const isDuplicate = name.trim() !== file?.name && siblingNames.includes(name.trim());
  const canSave = isNameValid && !isDuplicate;

  return (
    <>
      <Modal
        centered
        title="Edit File"
        footer={null}
        open={open && !showAnalyzing && !showConfig && !converting}
        onCancel={handleClose}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          className="common-form"
          name="edit-file-form"
          autoComplete="off"
        >
          {/* File Name */}
          <FormItemInputComponent
            label={<span style={{ fontWeight: 600 }}>File Name</span>}
            name="fileName"
            className=""
            onChangeFunction={handleNameChange}
            placeholder="Enter File Name"
            inputType="text"
            value={name}
            required={false}
            rules={[
              { required: true, message: 'File Name is required' },
              { max: 50, message: 'File Name must be max 50 characters.' },
              {
                pattern: new RegExp(
                  '^[A-Za-z0-9- _]*[A-Za-z0-9][A-Za-z0-9 _]*$',
                ),
                message: 'Special character not allowed',
              },
              {
                validator: (_: any, value: string) => {
                  if (value && value.trim() !== file?.name && siblingNames.includes(value.trim())) {
                    return Promise.reject(new Error('A file with this name already exists'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          />

          {/* File Dragger */}
          <Form.Item name="file" style={{ marginTop: 16, marginBottom: 8 }}>
            <Upload.Dragger
              accept=".dwg"
              fileList={fileList}
              maxCount={1}
              beforeUpload={beforeUpload}
              onChange={handleFileChange}
              onRemove={handleRemoveFile}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
            </Upload.Dragger>
          </Form.Item>

          {/* Re-convert checkbox - only show when no new file */}
          {!newFile && (
            <>
              <Checkbox
                checked={wantReconvert}
                onChange={(e) => setWantReconvert(e.target.checked)}
                style={{
                  marginBottom: wantReconvert ? 8 : 16,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                Re-convert with new settings{' '}
                <Tooltip
                  title="Change layers, units, or resolution"
                  placement="top"
                  color="#333"
                  overlayStyle={{ maxWidth: 400 }}
                  overlayInnerStyle={{ fontSize: 13 }}
                  zIndex={2000}
                >
                  <InfoCircleOutlined
                    style={{
                      fontSize: 14,
                      color: '#1890ff',
                      cursor: 'help',
                      marginLeft: 4,
                    }}
                  />
                </Tooltip>
              </Checkbox>
              {wantReconvert && (
                <div
                  style={{
                    padding: '8px 12px',
                    background: '#f5f5f5',
                    borderRadius: 4,
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span style={{ color: '#52c41a', fontSize: 16 }}>📎</span>
                  <span style={{ color: '#595959' }}>
                    Using existing file: <strong>{file?.name}.dwg</strong>
                  </span>
                </div>
              )}
            </>
          )}

          {/* Conversion options - show when uploading new file or want to reconvert */}
          {(newFile || wantReconvert) && (
            <div style={{ marginTop: newFile ? -16 : 0 }}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 600 }}>
                    Curved Element Resolution{' '}
                    <Tooltip
                      title="Higher values add more points along curved elements"
                      placement="top"
                      color="#333"
                      overlayStyle={{ maxWidth: 400 }}
                      overlayInnerStyle={{ fontSize: 13 }}
                      zIndex={2000}
                    >
                      <InfoCircleOutlined
                        style={{
                          fontSize: 14,
                          color: '#1890ff',
                          cursor: 'help',
                        }}
                      />
                    </Tooltip>
                  </span>
                }
                name="simplifyThreshold"
                required={false}
                rules={[
                  { required: true, message: 'Please select a Resolution!' },
                ]}
                initialValue={0.001}
              >
                <Select
                  placeholder="Select Resolution"
                  value={simplifyThreshold}
                  onChange={(value: number) => setSimplifyThreshold(value)}
                >
                  <Option value={0.0001}>High</Option>
                  <Option value={0.001}>Default</Option>
                  <Option value={0.01}>Low</Option>
                </Select>
              </Form.Item>

              <Form.Item style={{ marginBottom: 16 }}>
                <Checkbox
                  checked={keepAsBuilt}
                  onChange={(e) => setKeepAsBuilt(e.target.checked)}
                >
                  Keep as-built, custom layers and scanned points
                </Checkbox>
              </Form.Item>
            </div>
          )}

          {/* Footer Buttons */}
          <div className="form-btns">
            <Button className="self-btn" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              className="common-btn"
              onClick={handleSave}
              disabled={!canSave}
              loading={saving}
            >
              {newFile || wantReconvert ? 'Continue' : 'Save'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Analyzing Modal */}
      <AnalyzingModal open={showAnalyzing} uploadProgress={uploadProgress} />

      {/* Config Modal */}
      <DrawingConfigModal
        open={showConfig}
        analysisResult={analysisResult}
        onConfirm={handleConfigConfirm}
        onCancel={handleConfigCancel}
      />
    </>
  );
};

export default EditFileModal;
